import type { AssetMediaRef, TestedAssetType } from "./index";

/**
 * A link or file as an asset's content: the link is the asset's `url`, and a
 * landing page's link is also its `pageUrl` (the page the Save captures when
 * nothing shows it), unless the link is its video. The one rule for every
 * writer that hands a link over with or without its file: the collect card
 * and the goal intake (`collect_assets`, `buildIntakeTestedAssets`) and the
 * assets panel's "Add by URL" (E-session-14).
 */
export function collectedAssetContent(
  type: TestedAssetType,
  item: { url?: string; media: Array<AssetMediaRef> },
): { url?: string; pageUrl?: string; media: Array<AssetMediaRef> } {
  const page =
    savesCaptureOfLink(type) && !item.media.some((ref) => ref.kind === "video");
  return {
    ...(item.url && page ? { pageUrl: item.url } : {}),
    ...(item.url ? { url: item.url } : {}),
    media: item.media,
  };
}

/**
 * Whether a family's link alone is an asset the Save captures (its
 * `pageUrl`, D2 S5.1): a landing page. A page whose capture fails stays its
 * link, and readiness says what it needs (`page_capture_*`, owner A3), so a
 * writer hands the link over even when its own capture of it failed (H06-LP).
 * Any other family's link shows nothing unless a capture of it is attached.
 */
export function savesCaptureOfLink(type: TestedAssetType): boolean {
  return type === "landing_page";
}

/**
 * The web address an asset was given by: its `pageUrl`, else its `url`, else the
 * address its attached pictures keep (a captured page's `url_snapshot`), trimmed;
 * undefined for an asset given only as files or copy. The one reader of an asset's
 * link: the Save's capture, readiness, the collect's re-add check, the editor's
 * panel and the study state the stages read all call it, so none composes the link
 * from the fields itself. The asset's own address comes before the one a
 * picture keeps: an address edited on the asset is the link, whatever
 * snapshot an earlier capture left.
 */
export function assetLink(asset: unknown): string | undefined {
  const record = (asset ?? {}) as { pageUrl?: unknown; url?: unknown; media?: unknown };
  const media = Array.isArray(record.media)
    ? (record.media as Array<{ url?: unknown; sliceOf?: unknown }>)
    : [];
  const link = [
    record.pageUrl,
    record.url,
    ...media.filter((ref) => !ref?.sliceOf).map((ref) => ref?.url),
  ].find((value) => typeof value === "string" && value.trim() !== "");
  return typeof link === "string" ? link.trim() : undefined;
}
